/**
 * AlignmentGuideFeedback.tsx
 * Feedback visuel amélioré pour les guides d'alignement (style Figma)
 * UX: Indicateurs visuels clairs quand un élément s'aligne
 */
import React, { useEffect, useState } from 'react';
import { Line, Text, Group } from 'react-konva';
import Konva from 'konva';
import { useAppStore } from '../../../services/StateService';

interface AlignmentGuideFeedbackProps {
  stage: Konva.Stage;
  layer: Konva.Layer;
  alignmentGuides: Array<{
    points: () => number[];
    [key: string]: unknown;
  }>;
}

interface AlignmentLine {
  x?: number;
  y?: number;
  distance?: number;
  label?: string;
}

export const AlignmentGuideFeedback: React.FC<AlignmentGuideFeedbackProps> = ({
  stage,
  layer,
  alignmentGuides,
}) => {
  const [alignmentLines, setAlignmentLines] = useState<AlignmentLine[]>([]);
  const alignmentGuidesEnabled = useAppStore(state => state.canvas.alignmentGuidesEnabled);

  useEffect(() => {
    if (!alignmentGuides || !alignmentGuidesEnabled) {
      setAlignmentLines([]);
      return;
    }

    // Écouter les événements d'alignement
    const handleAlignment = () => {
      // Récupérer les guides actifs depuis AlignmentGuides
      const guides = layer.find('.alignmentGuide');
      const lines: AlignmentLine[] = [];

      // P3 : Konva.find() retourne Node<NodeConfig>[] ; on cast en any pour accéder
      // aux propriétés custom posées sur les guides (.points, .distance, .label).
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      guides.forEach((guide: any) => {
        const points = guide.points?.();
        if (points && points.length >= 4) {
          const isVertical = points[0] === points[2];
          lines.push({
            x: isVertical ? points[0] : undefined,
            y: isVertical ? undefined : points[1],
            distance: guide.distance || 0,
            label: guide.label || '',
          });
        }
      });

      setAlignmentLines(lines);
    };

    // Écouter les changements de position des nœuds
    const handleNodeMove = () => {
      handleAlignment();
    };

    stage.on('dragmove', handleNodeMove);
    stage.on('transform', handleNodeMove);

    return () => {
      stage.off('dragmove', handleNodeMove);
      stage.off('transform', handleNodeMove);
    };
  }, [stage, layer, alignmentGuides, alignmentGuidesEnabled]);

  if (!alignmentGuidesEnabled || alignmentLines.length === 0) {
    return null;
  }

  return (
    <Group name="alignmentFeedback">
      {alignmentLines.map((line, index) => (
        <Group key={index}>
          {/* Ligne d'alignement avec animation */}
          {line.x !== undefined && (
            <Line
              points={[line.x, 0, line.x, stage.height()]}
              stroke="#3b82f6"
              strokeWidth={2}
              dash={[8, 4]}
              opacity={0.8}
              listening={false}
              name="alignmentFeedbackLine"
            />
          )}
          {line.y !== undefined && (
            <Line
              points={[0, line.y, stage.width(), line.y]}
              stroke="#3b82f6"
              strokeWidth={2}
              dash={[8, 4]}
              opacity={0.8}
              listening={false}
              name="alignmentFeedbackLine"
            />
          )}
          {/* Label de distance */}
          {line.label && line.distance !== undefined && (
            <Text
              x={line.x !== undefined ? line.x + 10 : 10}
              y={line.y !== undefined ? line.y + 10 : 10}
              text={line.label}
              fontSize={12}
              fill="#3b82f6"
              padding={4}
              background="#ffffff"
              cornerRadius={4}
              listening={false}
              name="alignmentFeedbackLabel"
            />
          )}
        </Group>
      ))}
    </Group>
  );
};
